import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiX, FiDownload, FiTrash2, FiChevronLeft } from 'react-icons/fi';
import { getCharacterGallery, deletePhoto } from '../services/imageService';

const CharacterGallery = ({ isOpen, onClose, character, firebaseUser }) => {
  const [images, setImages] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const loaderRef = useRef(null);
  const isLoadingRef = useRef(false); // ref guard to prevent concurrent fetches
  // Track the character whose images are currently loaded so we can reset on change
  const loadedCharacterIdRef = useRef(null);

  const loadImages = useCallback(
    async (cursor = null) => {
      if (!firebaseUser || !character || isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const token = await firebaseUser.getIdToken();
        const characterId = character.characterId;
        const result = await getCharacterGallery(token, characterId, cursor, 20);
        if (cursor) {
          setImages((prev) => [...prev, ...(result.images || [])]);
        } else {
          setImages(result.images || []);
        }
        setNextCursor(result.nextCursor || null);
        setHasMore(!!result.nextCursor);
      } catch (err) {
        console.error('Error loading gallery:', err);
        setHasMore(false); // stop the observer from retrying on error
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firebaseUser, character]
  );

  // Reset and load fresh whenever the gallery opens or the character changes
  useEffect(() => {
    if (!isOpen || !character) return;
    const charId = character.characterId;
    if (loadedCharacterIdRef.current !== charId) {
      loadedCharacterIdRef.current = charId;
      isLoadingRef.current = false;
      setImages([]);
      setNextCursor(null);
      setHasMore(true);
      setSelectedImage(null);
    }
    loadImages(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, character]);

  // IntersectionObserver sentinel for infinite scroll
  useEffect(() => {
    const sentinel = loaderRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadImages(nextCursor);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, nextCursor, loadImages]);

  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    setDeletingId(imageId);
    try {
      const token = await firebaseUser.getIdToken();
      await deletePhoto(token, imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      if (selectedImage?.id === imageId) setSelectedImage(null);
    } catch (err) {
      console.error('Error deleting photo:', err);
      alert('Failed to delete photo.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (imageUrl, imageId) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photo-${imageId}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading photo:', err);
    }
  };

  if (!isOpen) return null;

  const charName = character?.name || 'Character';

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          {selectedImage ? (
            <button
              onClick={() => setSelectedImage(null)}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
            >
              <FiChevronLeft size={18} />
              Back to Gallery
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">
              {charName}&apos;s Gallery
            </h2>
          )}
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-800 transition-colors"
            aria-label="Close gallery"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* Lightbox view */}
        {selectedImage ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-auto bg-gray-50">
            <img
              src={selectedImage.url}
              alt="Full size"
              className="max-w-full max-h-[60vh] rounded-lg object-contain shadow-lg"
            />
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => handleDownload(selectedImage.url, selectedImage.id)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
              >
                <FiDownload size={15} />
                Download
              </button>
              <button
                onClick={() => handleDelete(selectedImage.id)}
                disabled={deletingId === selectedImage.id}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 text-sm font-medium transition-colors disabled:opacity-50"
              >
                <FiTrash2 size={15} />
                {deletingId === selectedImage.id ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        ) : (
          /* Grid view */
          <div className="flex-1 overflow-y-auto p-4">
            {images.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <p className="text-base font-medium">No photos yet.</p>
                <p className="text-sm mt-1">Ask {charName} to share a photo!</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              {images.map((img) => (
                <div
                  key={img.id}
                  className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => setSelectedImage(img)}
                >
                  <img
                    src={img.thumbnailUrl || img.url}
                    alt="Gallery"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Hover overlay with action buttons */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-150 flex items-end justify-end p-2 gap-1.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(img.url, img.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white bg-opacity-90 rounded text-gray-700 hover:text-blue-600 hover:bg-opacity-100"
                      title="Download"
                    >
                      <FiDownload size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(img.id);
                      }}
                      disabled={deletingId === img.id}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white bg-opacity-90 rounded text-red-500 hover:text-red-700 hover:bg-opacity-100 disabled:opacity-50"
                      title="Delete"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Infinite scroll sentinel / loading indicator */}
            <div ref={loaderRef} className="flex justify-center py-6">
              {isLoading && (
                <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CharacterGallery;
